import { useState } from "react";
import { useFetcher } from "react-router";
import { useForm } from "react-hook-form";
import { EMAIL_REGEX } from "#app/utils/regex.js";
import { useEmailSuggestion } from "#app/hooks/index.js";

import {
  Icon,
  Box,
  Field,
  Flex,
  Heading,
  Input,
  Link,
  Text,
} from "@chakra-ui/react";
import { Button } from "#app/components/ui/button.jsx";
import { MdCheckCircle } from "react-icons/md";

/**
 * Inline email capture for blog posts. Compact card style that sits between
 * markdown sections. Uses the blog route's action with intent "emailCapture".
 */
export function BlogEmailCapture({ config }) {
  const fetcher = useFetcher();
  const [dismissed, setDismissed] = useState(false);
  const isLoading =
    fetcher.state === "submitting" || fetcher.state === "loading";
  const isSuccess =
    !dismissed &&
    fetcher.state === "idle" &&
    fetcher.data != null &&
    fetcher.data?.success;

  const formContext = useForm({ defaultValues: { email: "", website: "" } });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = formContext;

  const { suggestion, checkEmail, clearSuggestion } = useEmailSuggestion();

  const onSubmit = (data) => {
    setDismissed(false);
    const attribution = window.SchnerpAttribution || {};

    fetcher.submit(
      {
        intent: "emailCapture",
        email: data.email,
        website: data.website || "",
        utmSource: attribution.utmSource || "",
        utmMedium: attribution.utmMedium || "",
        utmCampaign: attribution.utmCampaign || "",
        utmContent: attribution.utmContent || "",
      },
      { method: "post" },
    );
  };

  const emailRegistration = register("email", {
    required: { value: true, message: "Please enter an email address" },
    pattern: { value: EMAIL_REGEX, message: "Invalid email address" },
    onBlur: (e) => checkEmail(e.target.value),
    onChange: () => clearSuggestion(),
  });

  return (
    <Box
      bg="bg.subtle"
      border="1px"
      borderColor="border"
      rounded="xl"
      p={6}
      my={8}
    >
      {config.heading && (
        <Heading as="h3" size="xs" mb={2}>
          {config.heading}
        </Heading>
      )}
      {config.description && (
        <Text color="muted" fontSize="sm" mb={4}>
          {config.description}
        </Text>
      )}
      {isSuccess ? (
        <Flex align="center" gap={2}>
          <Icon as={MdCheckCircle} color="green.400" />
          <Text color="muted" fontSize="sm">
            You're on the list! We'll keep you posted.
          </Text>
          <Button
            variant="link"
            size="sm"
            color="muted"
            fontWeight="normal"
            onClick={() => {
              setDismissed(true);
              formContext.reset();
            }}
          >
            Add another
          </Button>
        </Flex>
      ) : (
        <Box as="form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box position="absolute" left="-9999px" aria-hidden="true">
            <Input tabIndex={-1} autoComplete="off" {...register("website")} />
          </Box>
          <Field.Root invalid={!!errors.email}>
            <Field.Label srOnly>Email address</Field.Label>
            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={2}
              align="start"
            >
              <Box flex={1} w="100%">
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  size="md"
                  bg="bg-surface"
                  {...emailRegistration}
                />
                {errors.email && (
                  <Field.ErrorText>{errors.email.message}</Field.ErrorText>
                )}
                {!errors.email && suggestion && (
                  <Text fontSize="sm" color="warning" mt={1} textAlign="left">
                    Did you mean{" "}
                    <Link
                      color="warning"
                      fontWeight="bold"
                      textDecoration="underline"
                      cursor="pointer"
                      onClick={() => {
                        formContext.setValue("email", suggestion, {
                          shouldValidate: true,
                        });
                        clearSuggestion();
                      }}
                    >
                      {suggestion}
                    </Link>
                    ?
                  </Text>
                )}
              </Box>
              <Button
                variant="primary"
                size="md"
                type="submit"
                loading={isLoading}
                fontWeight="extrabold"
                flexShrink={0}
                w={{ base: "100%", sm: "auto" }}
              >
                Keep me posted
              </Button>
            </Flex>
          </Field.Root>
        </Box>
      )}
    </Box>
  );
}
